import { javaDoubleToString } from './javaText.ts';

/**
 * The desktop's `export.JsonWriter`: two-space indents, `{}` and `[]` when empty, and only
 * `"`, `\`, line breaks, tabs and control characters escaped. Shared by the JSON export and
 * the random-table codec, so both write what the desktop writes. Numbers are whole (Java
 * ints and longs), unless wrapped as a `JsonDouble`, which writes as Java's `Double.toString`.
 */

/** A Java `double`: "435.0", "0.00125", "1.0E-4". */
export class JsonDouble {
  constructor(readonly value: number) {}
}

/** An object is a `Map`, so its keys keep their order even when they look like numbers. */
export type JsonValue = string | number | boolean | null | JsonDouble | readonly JsonValue[] | ReadonlyMap<string, JsonValue>;

/** `JsonWriter.write`: the value, then a line feed. */
export function writeJson(value: JsonValue): string {
  return `${write(value, 0)}\n`;
}

function write(value: JsonValue, indent: number): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return quote(value);
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (value instanceof JsonDouble) return javaDoubleToString(value.value);
  const pad = '  '.repeat(indent + 1);
  const end = '  '.repeat(indent);
  if (value instanceof Map) {
    if (value.size === 0) return '{}';
    const items = [...value].map(([key, item]) => `${pad}${quote(key)}: ${write(item, indent + 1)}`);
    return `{\n${items.join(',\n')}\n${end}}`;
  }
  const list = value as readonly JsonValue[];
  if (list.length === 0) return '[]';
  return `[\n${list.map((item) => `${pad}${write(item, indent + 1)}`).join(',\n')}\n${end}]`;
}

function quote(value: string): string {
  let out = '"';
  for (const c of value) {
    const code = c.charCodeAt(0);
    if (c === '"') out += '\\"';
    else if (c === '\\') out += '\\\\';
    else if (c === '\n') out += '\\n';
    else if (c === '\r') out += '\\r';
    else if (c === '\t') out += '\\t';
    else if (code < 0x20) out += `\\u${code.toString(16).padStart(4, '0')}`;
    else out += c;
  }
  return `${out}"`;
}
